import { z } from 'zod';
import { calibrationSampleSchema, macroVisualDigestSchema, macroVisualPresetSchema, macroVisualProfileSchema,
  visualAnalysisErrorCodeSchema, VISUAL_COMPARISON_LIMIT } from './common';
import { analysisProgressSchema, analysisStatusSchema, blobAssetSchema, chunkReceiptSchema,
  leaseSchema, readGrantSchema, sourceImageAssetSchema } from './jobs';
import { backendIdentitySchema, referenceArtifactRowSchema } from './artifacts';
import { calibrationEvidenceIndexAssetSchema } from './evidencePages';

/** Capabilities-only. An artifact backend stays `backendIdentitySchema` and is never unavailable. */
export const unavailableBackendSchema = z.object({
  backend: z.literal('d3d11'),
  deviceType: z.literal('unavailable'),
  decodePolicy: z.literal('opaque-sdr-no-display-icc-v1'),
  errorCode: visualAnalysisErrorCodeSchema.optional(),
}).strict();
export const analysisCapabilitiesSchema = z.object({
  backend: z.union([backendIdentitySchema, unavailableBackendSchema]),
  hardwareGate: z.enum(['available', 'skipped', 'error']),
  maxResidentVariants: z.number().int().positive().max(8),
  schemaVersions: z.array(z.literal(2)).min(1).max(16),
  profiles: z.array(macroVisualProfileSchema).min(1).max(16),
  presets: z.array(macroVisualPresetSchema).min(1).max(16),
  maxSourcePixels: z.number().int().positive().max(16_777_216),
  maxGpuBytes: z.number().int().positive().max(134_217_728),
  maxComparisons: z.number().int().positive().max(VISUAL_COMPARISON_LIMIT),
  maxReadbacks: z.number().int().positive().max(16),
}).strict();
export const previewRequestSchema = z.object({
  caseId: z.string().min(1).max(128),
  kind: z.enum(['source', 'variant', 'heatmap']),
  maxEdge: z.number().int().min(1).max(512),
}).strict();
const requestId = z.string().min(1).max(128);
const assetAccess = {
  assetGrantEndpoint: z.string().url().max(2048),
  assetToken: z.string().min(1).max(4096),
};
export const analysisCapabilitiesCommandSchema = z.object({ command: z.literal('analysisCapabilities'), requestId }).strict();
export const analysisStartCommandSchema = z.object({ command: z.literal('analysisStart'), requestId, lease: leaseSchema,
  manifest: readGrantSchema, acknowledged: z.array(chunkReceiptSchema).max(8192), ...assetAccess }).strict();
export const analysisPreviewCommandSchema = z.object({ command: z.literal('analysisPreview'), requestId, lease: leaseSchema,
  manifest: readGrantSchema, preview: previewRequestSchema, ...assetAccess }).strict();
export const analysisRenewCommandSchema = z.object({ command: z.literal('analysisRenew'), requestId, lease: leaseSchema }).strict();
export const analysisStatusCommandSchema = z.object({ command: z.literal('analysisStatus'), requestId, lease: leaseSchema }).strict();
export const analysisCancelCommandSchema = z.object({ command: z.literal('analysisCancel'), requestId, lease: leaseSchema }).strict();
export const analysisCommandSchema = z.discriminatedUnion('command', [
  analysisCapabilitiesCommandSchema, analysisStartCommandSchema, analysisPreviewCommandSchema,
  analysisRenewCommandSchema, analysisStatusCommandSchema, analysisCancelCommandSchema,
]);
export const chunkDescriptorSchema = blobAssetSchema.extend({
  byteCount: z.number().int().positive().max(1_048_576),
  sequence: z.number().int().nonnegative().max(8191),
  firstCaseId: z.string().min(1).max(128),
  lastCaseId: z.string().min(1).max(128),
  rowCount: z.number().int().min(1).max(128),
  bindingDigest: macroVisualDigestSchema,
}).strict();
export const completionRootSchema = z.object({
  /**
   * Native evidence closure for source-graph publishing. Standalone visual tests
   * intentionally do not build the source-graph evidence index, but still return
   * the complete reference readiness and threshold result needed for replay.
   */
  evidenceIndex: calibrationEvidenceIndexAssetSchema.optional(),
  orderedChunks: z.array(chunkReceiptSchema).max(8192),
  referencePages: z.array(blobAssetSchema).max(512),
  coverageDigest: macroVisualDigestSchema,
  backend: backendIdentitySchema,
  referenceReadiness: z.array(z.object({ referenceId: macroVisualDigestSchema,
    readiness: z.string().min(1).max(32), errorCode: z.string().max(256).optional(),
  }).strict()).max(16384).optional(),
  /** Per node; `poorQuality` when any bound reference's `trainingQuality.poor` is set. */
  recommendedThresholds: z.array(z.object({
    nodeId: z.string().min(1).max(128),
    matchThresholdPercent: z.number().int().min(0).max(100),
    poorQuality: z.boolean().optional(),
  }).strict()).max(16384).optional(),
  /** Lets a playback subset derive its own node scale without importing training-only references. */
  referenceRecommendedThresholds: z.array(z.object({
    referenceId: macroVisualDigestSchema,
    matchThresholdPercent: z.number().int().min(0).max(100),
    poorQuality: z.boolean().optional(),
  }).strict()).max(16384).optional(),
}).strict();
const replyIdentity = { kind: z.literal('analysis'), requestId, inputsSent: z.literal(false) };
export const analysisCapabilitiesReplySchema = z.object({ ...replyIdentity,
  status: z.literal('capabilities'), capabilities: analysisCapabilitiesSchema,
}).strict();
export const analysisJobReplySchema = z.object({ ...replyIdentity,
  status: analysisStatusSchema,
  lease: leaseSchema,
  sequence: z.number().int().nonnegative(),
  progress: analysisProgressSchema.optional(),
  chunk: chunkDescriptorSchema.optional(),
  completion: completionRootSchema.optional(),
  previewAsset: blobAssetSchema.optional(),
  errorCode: visualAnalysisErrorCodeSchema.optional(),
}).strict().superRefine((reply, context) => {
  if (reply.status === 'completed' && !reply.completion) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['completion'], message: 'Successful completion requires the pinned evidence index' });
  }
});
export const analysisReplySchema = z.union([analysisCapabilitiesReplySchema, analysisJobReplySchema]);

export type AnalysisCapabilities = z.infer<typeof analysisCapabilitiesSchema>;
export type AnalysisCommand = z.infer<typeof analysisCommandSchema>;
export type AnalysisReply = z.infer<typeof analysisReplySchema>;
export const renderedAnalysisEvidenceSchema = z.object({ caseId: z.string().min(1).max(128),
  source: sourceImageAssetSchema, rendered: blobAssetSchema, transformVersion: z.string().min(1).max(128),
  parameters: z.record(z.union([z.number().finite(), z.string().max(512), z.boolean()])) }).strict();
export type RenderedAnalysisEvidence = z.infer<typeof renderedAnalysisEvidenceSchema>;
export const resultPageSchema = z.object({ rows: z.array(calibrationSampleSchema).max(128),
  evidence: z.array(renderedAnalysisEvidenceSchema).max(128).optional(),
  nextCursor: z.string().min(1).max(2048).optional() }).strict();
export const previewProgressSchema = z.object({
  previewId: z.string().min(1).max(128), status: z.enum(['queued', 'waiting_for_gpu_host', 'running', 'completed', 'failed', 'cancelled']),
  asset: readGrantSchema.optional(), errorCode: visualAnalysisErrorCodeSchema.optional(),
}).strict();
export type PreviewRequest = z.infer<typeof previewRequestSchema>;
export type PreviewProgress = z.infer<typeof previewProgressSchema>;
export type ResultPage = z.infer<typeof resultPageSchema>;
export const referenceResultPageSchema = z.object({ rows: z.array(referenceArtifactRowSchema).max(128),
  nextCursor: z.string().min(1).max(2048).optional() }).strict();
export type ReferenceResultPage = z.infer<typeof referenceResultPageSchema>;
export type AnalysisChunkDescriptor = z.infer<typeof chunkDescriptorSchema>;
export type AnalysisCompletionRoot = z.infer<typeof completionRootSchema>;
